import { useState, useEffect } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMapEvents,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import SearchBar from "../../components/SearchBar";

const markerIcon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.7/dist/images/marker-icon.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

const defaultPosition: [number, number] = [4.175278, 73.508889]; // Malé

interface LocationSelectorProps {
  value?: [number, number] | null;
  onChange?: (lat: number, lng: number) => void;
  height?: number;
}

/* ---------------- MAP CLICK HANDLER ---------------- */
function LocationPicker({
  onSelect,
}: {
  onSelect: (pos: [number, number]) => void;
}) {
  useMapEvents({
    click(e) {
      onSelect([e.latlng.lat, e.latlng.lng]);
    },
  });
  return null;
}

/* ---------------- MAP CENTER SYNC ---------------- */
function MapCenterUpdater({ position }: { position: [number, number] }) {
  const map = useMap();

  useEffect(() => {
    map.setView(position, map.getZoom(), { animate: true });
  }, [position, map]);

  return null;
}

/* ---------------- LOCATION SELECTOR ---------------- */
export default function LocationSelector({
  value = null,
  onChange,
  height = 360,
}: LocationSelectorProps) {
  const [userLocation, setUserLocation] =
    useState<[number, number]>(defaultPosition);

  const [selectedPos, setSelectedPos] =
    useState<[number, number] | null>(value);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<
    { display_name: string; lat: string; lon: string }[]
  >([]);

  /* ---- Sync external value ---- */
  useEffect(() => {
    if (value) setSelectedPos(value);
  }, [value]);

  /* ---- Get user GPS ---- */
  useEffect(() => {
    navigator.geolocation?.getCurrentPosition(
      (pos) =>
        setUserLocation([pos.coords.latitude, pos.coords.longitude]),
      () => setUserLocation(defaultPosition)
    );
  }, []);

  /* ---- Handle search ---- */
  useEffect(() => {
    if (!searchQuery) return setSearchResults([]);

    const controller = new AbortController();
    const signal = controller.signal;

    fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
        searchQuery
      )}`,
      { signal }
    )
      .then((res) => res.json())
      .then((data) => setSearchResults(data))
      .catch(() => {
        if (!signal.aborted) setSearchResults([]);
      });

    return () => controller.abort();
  }, [searchQuery]);

  const handleSelect = (pos: [number, number]) => {
    setSelectedPos(pos);
    onChange?.(pos[0], pos[1]); // ✅ parent receives values
  };

  const handleSearchSelect = (lat: string, lon: string) => {
    const position: [number, number] = [parseFloat(lat), parseFloat(lon)];
    handleSelect(position);
    setSearchQuery("");
    setSearchResults([]);
  };

  const center = selectedPos ?? userLocation;

  return (
    <div className="flex flex-col gap-3 w-full">
      {/* Search Box */}
      <div className="flex flex-col gap-1">
        <SearchBar onSearch={(val)=> setSearchQuery(val)} />
        {/* <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search for a location..."
          className="px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring focus:border-blue-300 dark:bg-gray-700 dark:text-gray-100"
        /> */}
        {searchResults.length > 0 && (
          <div className="bg-white dark:bg-gray-800 border rounded-md max-h-60 overflow-y-auto">
            {searchResults.map((r, idx) => (
              <div
                key={idx}
                onClick={() => handleSearchSelect(r.lat, r.lon)}
                className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-sm"
              >
                {r.display_name}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Map */}
      <div
        className="w-full rounded-xl overflow-hidden border shadow-sm"
        style={{ height }}
      >
        <MapContainer center={center} zoom={12} className="h-full w-full">
          <TileLayer
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution="© OpenStreetMap contributors"
          />

          <LocationPicker onSelect={handleSelect} />

          <MapCenterUpdater position={center} />

          {selectedPos && <Marker position={selectedPos} icon={markerIcon} />}
        </MapContainer>
      </div>

      {/* Selected Coordinates */}
      {selectedPos && (
        <div className="text-sm text-gray-700 bg-gray-50 p-2 rounded-md border">
          📍 <strong>Selected:</strong>{" "}
          {selectedPos[0].toFixed(5)}, {selectedPos[1].toFixed(5)}
        </div>
      )}
    </div>
  );
}